import React from 'react'

import { BorderStyle, Box, Color, Display, FlexDirection } from '@gousto-internal/citrus-react'
import type {
  DashboardExperienceOrderAdditionResponse,
  DashboardExperienceOrderNewAndLimitedResponse,
  DashboardExperienceOrderTopPicksResponse,
} from '@library/api-dashboard-experience'

import {
  RecommendationSection,
  type RecommendationSectionData,
} from './RecommendationSection/RecommendationSection'
// TODO: need to add this to the customerengagment xapi unto top_picks
const TOP_PICKS_SEE_MORE_LABEL = 'See more'

type OrderRecommendationsProps = {
  topPicks: DashboardExperienceOrderTopPicksResponse | null
  newAndLimited: DashboardExperienceOrderNewAndLimitedResponse | null
  additions?: DashboardExperienceOrderAdditionResponse[]
  orderId: string
}

const topPicksSection = (
  topPicks: DashboardExperienceOrderTopPicksResponse,
): RecommendationSectionData => ({
  collection: 'top picks for you',
  id: 'top-picks',
  seeMoreLabel: TOP_PICKS_SEE_MORE_LABEL,
  title: topPicks.title,
  recipes: topPicks.recipes,
})

const newAndLimitedSection = (
  newAndLimited: DashboardExperienceOrderNewAndLimitedResponse,
): RecommendationSectionData => ({
  collection: 'new and limited edition',
  id: 'new-and-limited',
  seeMoreLabel: newAndLimited.see_more_label,
  title: newAndLimited.title,
  recipes: newAndLimited.recipes,
})

const getSectionsFromAdditions = (
  additions: DashboardExperienceOrderAdditionResponse[],
): RecommendationSectionData[] =>
  additions.map((addition) => {
    if (addition.type === 'recipe_highlights') {
      return topPicksSection(addition.data)
    }

    return newAndLimitedSection(addition.data)
  })

const getSections = ({
  additions,
  topPicks,
  newAndLimited,
}: Pick<
  OrderRecommendationsProps,
  'additions' | 'topPicks' | 'newAndLimited'
>): RecommendationSectionData[] => {
  const sections: Array<RecommendationSectionData | null> = additions?.length
    ? getSectionsFromAdditions(additions)
    : [
        topPicks ? topPicksSection(topPicks) : null,
        newAndLimited ? newAndLimitedSection(newAndLimited) : null,
      ]

  return sections.filter((section): section is RecommendationSectionData =>
    Boolean(section && section.recipes.length > 0),
  )
}

export const OrderRecommendations = ({
  additions,
  topPicks,
  newAndLimited,
  orderId,
}: OrderRecommendationsProps) => {
  const sections = getSections({
    additions,
    topPicks,
    newAndLimited,
  })

  if (sections.length === 0) {
    return null
  }

  return (
    <Box
      data-testid="order-recommendations"
      width="100%"
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={4}
      borderWidth={0.5}
      borderStyle={BorderStyle.Solid}
      borderColor={Color.ColdGrey_100}
      paddingBottom={4}
      paddingH={4}
      paddingV={4}
      paddingRight={4}
      bg={Color.White}
      style={{
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
        overflow: 'hidden',
      }}
    >
      {sections.map((section) => (
        <RecommendationSection key={section.id} orderId={orderId} section={section} />
      ))}
    </Box>
  )
}
